import { useState } from 'react';

import type { ErrorLike } from '@apollo/client';
import { useMutation } from '@apollo/client/react';
import {
  Box,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Ban,
  Crown,
  MoreHorizontal,
  PlayCircle,
  Plus,
  Star,
  Trash2,
  UserMinus,
  Users as UsersIcon,
  X,
} from 'lucide-react';
import { Link } from 'react-router-dom';

import type { GetUserQuery } from '@graphql/__generated__/graphql';
import { ListingAccordion } from '@modules/_shared/components/ListingAccordion';
import { MutationErrorSnackbar } from '@modules/_shared/components/MutationErrorModal';
import { SlideDialog } from '@modules/_shared/components/SlideDialog';
import { TeamsSelect } from '@modules/_shared/components/teams-select/TeamsSelect';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import {
  ADD_TEAM_USER,
  DELETE_TEAM_USER,
  TEAMS_CARD_UPDATE_USER_DEFAULT_TEAM_MUTATION,
  UPDATE_TEAM_USER,
} from '@modules/teams/teamQueries';
import { useCanViewSubscriptions } from '@modules/users/user-overview/hooks/useCanViewSubscriptions';
import { useExpandableCard } from '@modules/users/user-overview/hooks/useExpandableCard';
import { GET_USER } from '@modules/users/userQueries';
import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

type UserTeamsCardProps = {
  user: NonNullable<GetUserQuery['users_by_pk']>;
};

type TeamRow = {
  id: string;
  name: string;
  isLeader: boolean;
  isDeactivated: boolean;
};

type StatusBadgeProps = {
  show: boolean;
  dimmed: boolean;
  title: string;
  color: string;
  children: React.ReactNode;
};

const StatusBadge = ({
  show,
  dimmed,
  title,
  color,
  children,
}: StatusBadgeProps) => {
  if (!show) {
    return null;
  }
  return (
    <Tooltip title={title}>
      <Box
        sx={{
          display: 'flex',
          color,
          opacity: dimmed ? 0.5 : 1,
          transition: 'opacity 0.15s ease',
        }}
      >
        {children}
      </Box>
    </Tooltip>
  );
};

export const UserTeamsCard = ({ user }: UserTeamsCardProps) => {
  const { t } = useLocale();
  const { me } = useAppData();
  const isAdmin = me?.is_admin ?? false;
  const canViewSubscriptions = useCanViewSubscriptions(user.id);

  const teams: TeamRow[] = (user.teams_users ?? [])
    .filter(tu => tu.team != null)
    .map(tu => ({
      id: tu.team!.id,
      name: tu.team!.name,
      isLeader: tu.is_leader,
      isDeactivated: tu.deactivated,
    }));
  const defaultTeamId = user.default_team_id ?? user.default_team?.id ?? null;
  const isSelf = me?.id === user.id;

  const canManageLeaderFor = (teamId: string) =>
    isAdmin ||
    (me?.teams?.find(team => team.team_id === teamId)?.is_leader ?? false);

  const canManageAnyTeam =
    isAdmin || (me?.teams?.some(team => team.is_leader) ?? false);

  const { expand, accordionProps } = useExpandableCard();
  const [apolloError, setApolloError] = useState<ErrorLike | null>(null);
  const [adding, setAdding] = useState(false);
  const [menuFor, setMenuFor] = useState<{
    anchor: HTMLElement;
    teamId: string;
  } | null>(null);
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null);

  const mutationOptions = {
    refetchQueries: [
      {
        query: GET_USER,
        variables: { id: user.id, includeSubscriptions: canViewSubscriptions },
      },
      'GetUserNotificationSettings',
    ],
    awaitRefetchQueries: true,
    onError: setApolloError,
  };

  const [addTeamUser, { loading: addingLoading }] = useMutation(
    ADD_TEAM_USER,
    mutationOptions,
  );
  const [removeTeamUser, { loading: removingLoading }] = useMutation(
    DELETE_TEAM_USER,
    mutationOptions,
  );
  const [updateDefaultTeam] = useMutation(
    TEAMS_CARD_UPDATE_USER_DEFAULT_TEAM_MUTATION,
    mutationOptions,
  );
  const [updateTeamUser] = useMutation(UPDATE_TEAM_USER, mutationOptions);

  const [pendingTeams, setPendingTeams] = useState<
    ReadonlyMap<string, 'leader' | 'activation' | 'default'>
  >(new Map());
  const runTeam = (
    teamId: string,
    kind: 'leader' | 'activation' | 'default',
    promise: Promise<unknown>,
  ): Promise<void> => {
    setPendingTeams(prev => new Map(prev).set(teamId, kind));
    return promise
      .then(
        () => {},
        () => {},
      )
      .finally(() =>
        setPendingTeams(prev => {
          const next = new Map(prev);
          next.delete(teamId);
          return next;
        }),
      );
  };

  const setLeader = (teamId: string, isLeader: boolean) =>
    runTeam(
      teamId,
      'leader',
      updateTeamUser({
        variables: {
          user_id: user.id,
          team_id: teamId,
          team_user: { is_leader: isLeader },
        },
      }),
    );

  const setDeactivated = (teamId: string, deactivated: boolean) =>
    runTeam(
      teamId,
      'activation',
      updateTeamUser({
        variables: {
          user_id: user.id,
          team_id: teamId,
          team_user: deactivated
            ? { deactivated: true, is_leader: false }
            : { deactivated: false },
        },
      }),
    );

  const excludeIds = teams.map(team => team.id);

  const teamForConfirm = teams.find(team => team.id === confirmRemoveId);

  const closeMenu = () => setMenuFor(null);

  return (
    <ListingAccordion
      title={t('Teams')}
      {...accordionProps}
      headerAction={
        canManageAnyTeam ? (
          <Tooltip title={t('Add team')}>
            <IconButton
              size="small"
              onClick={() => {
                expand();
                setAdding(true);
              }}
              disabled={addingLoading || adding}
              sx={{
                color: 'primary.main',
                backgroundColor: 'action.hover',
                transition: 'all 0.15s ease',
                '&:hover': { backgroundColor: 'action.selected' },
              }}
            >
              <Plus size={16} />
            </IconButton>
          </Tooltip>
        ) : undefined
      }
    >
      <Stack sx={{ gap: 0.75 }}>
        {teams.length === 0 && (
          <Typography variant="body2" sx={{ color: 'text.disabled' }}>
            {isAdmin && !user.is_broker
              ? t('Not in any team yet - add one to promote to agent.')
              : t('Not in any team yet')}
          </Typography>
        )}

        {teams.map(team => {
          const isDefault = team.id === defaultTeamId;
          const hasRowActions = canManageLeaderFor(team.id);
          const pendingKind = pendingTeams.get(team.id);
          return (
            <Box
              key={team.id}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minHeight: 28,
                minWidth: 0,
              }}
            >
              <Box
                sx={{
                  color: 'text.secondary',
                  display: 'flex',
                  flexShrink: 0,
                  width: 16,
                  opacity: team.isDeactivated ? 0.45 : 1,
                }}
              >
                <UsersIcon size={16} />
              </Box>
              <Typography
                component={Link}
                to={`/teams/${team.id}/overview`}
                target="_blank"
                rel="noopener noreferrer"
                variant="body2"
                noWrap
                sx={{
                  color: 'text.primary',
                  flex: 1,
                  minWidth: 0,
                  textDecoration: 'none',
                  opacity: team.isDeactivated ? 0.55 : 1,
                  '&:hover': {
                    textDecoration: 'underline',
                    color: 'primary.main',
                  },
                }}
              >
                {team.name ?? t('Unnamed team')}
              </Typography>
              <StatusBadge
                show={pendingKind === 'activation' || team.isDeactivated}
                dimmed={pendingKind === 'activation'}
                title={t('Deactivated in this team')}
                color="text.disabled"
              >
                <Ban size={12} strokeWidth={2.5} />
              </StatusBadge>
              <StatusBadge
                show={
                  pendingKind === 'leader' ||
                  (!team.isDeactivated && team.isLeader)
                }
                dimmed={pendingKind === 'leader'}
                title={t('Team leader')}
                color="warning.main"
              >
                <Crown size={12} color="currentColor" strokeWidth={2.5} />
              </StatusBadge>
              <StatusBadge
                show={
                  pendingKind === 'default' ||
                  (!team.isDeactivated && isDefault)
                }
                dimmed={pendingKind === 'default'}
                title={t('Default team')}
                color="primary.main"
              >
                <Star size={12} fill="currentColor" strokeWidth={0} />
              </StatusBadge>
              {hasRowActions && (
                <IconButton
                  size="small"
                  onClick={evt =>
                    setMenuFor({ anchor: evt.currentTarget, teamId: team.id })
                  }
                  sx={{
                    width: 24,
                    height: 24,
                    color: 'text.secondary',
                    '&:hover': { backgroundColor: 'action.hover' },
                  }}
                >
                  <MoreHorizontal size={14} />
                </IconButton>
              )}
            </Box>
          );
        })}

        {adding && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1,
              minWidth: 0,
            }}
          >
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <TeamsSelect
                size="small"
                autoFocus
                openOnFocus
                blurOnSelect
                fullWidth
                excludeTeamsIds={excludeIds}
                onChange={async teamId => {
                  if (!teamId) {
                    return;
                  }
                  await addTeamUser({
                    variables: { user_id: user.id, team_id: teamId },
                  });
                  setAdding(false);
                }}
                disabled={addingLoading}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    background: customPalette.white,
                    borderRadius: '8px',
                    paddingTop: '2px',
                    paddingBottom: '2px',
                  },
                }}
              />
            </Box>
            <IconButton
              size="small"
              onClick={() => setAdding(false)}
              disabled={addingLoading}
              sx={{
                width: 28,
                height: 28,
                mt: '2px',
                color: 'text.secondary',
                '&:hover': { backgroundColor: 'action.hover' },
              }}
              title={t('Cancel')}
            >
              <X size={16} />
            </IconButton>
          </Box>
        )}
      </Stack>

      <Menu
        anchorEl={menuFor?.anchor ?? null}
        open={Boolean(menuFor)}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {menuFor &&
          (() => {
            const team = teams.find(row => row.id === menuFor.teamId);
            if (!team) {
              return null;
            }
            const canManageLeader = canManageLeaderFor(team.id);
            const isPending = pendingTeams.has(team.id);
            const act = (fn: () => Promise<void>) => async () => {
              closeMenu();
              await fn();
            };
            const items: React.ReactNode[] = [];

            if (!team.isDeactivated && canManageLeader && !team.isLeader) {
              items.push(
                <MenuItem
                  key="promote-leader"
                  disabled={isPending}
                  onClick={act(() => setLeader(team.id, true))}
                >
                  <ListItemIcon sx={{ color: 'warning.main' }}>
                    <Crown size={14} />
                  </ListItemIcon>
                  <ListItemText>{t('Make team leader')}</ListItemText>
                </MenuItem>,
              );
            }

            if (
              !team.isDeactivated &&
              canManageLeader &&
              team.isLeader &&
              !isSelf
            ) {
              items.push(
                <MenuItem
                  key="demote-leader"
                  disabled={isPending}
                  onClick={act(() => setLeader(team.id, false))}
                >
                  <ListItemIcon>
                    <UserMinus size={14} />
                  </ListItemIcon>
                  <ListItemText>{t('Remove leader rights')}</ListItemText>
                </MenuItem>,
              );
            }

            if (
              !team.isDeactivated &&
              team.id !== defaultTeamId &&
              canManageLeader
            ) {
              items.push(
                <MenuItem
                  key="make-default"
                  disabled={isPending}
                  onClick={act(() =>
                    runTeam(
                      team.id,
                      'default',
                      updateDefaultTeam({
                        variables: { user_id: user.id, team_id: team.id },
                      }),
                    ),
                  )}
                >
                  <ListItemIcon>
                    <Star size={14} />
                  </ListItemIcon>
                  <ListItemText>{t('Make default team')}</ListItemText>
                </MenuItem>,
              );
            }

            if (canManageLeader && team.isDeactivated) {
              items.push(
                <MenuItem
                  key="reactivate"
                  disabled={isPending}
                  onClick={act(() => setDeactivated(team.id, false))}
                >
                  <ListItemIcon sx={{ color: 'success.main' }}>
                    <PlayCircle size={14} />
                  </ListItemIcon>
                  <ListItemText>{t('Reactivate in team')}</ListItemText>
                </MenuItem>,
              );
            }

            if (canManageLeader && !team.isDeactivated && !isSelf) {
              items.push(
                <MenuItem
                  key="deactivate"
                  disabled={isPending}
                  onClick={act(() => setDeactivated(team.id, true))}
                >
                  <ListItemIcon>
                    <Ban size={14} />
                  </ListItemIcon>
                  <ListItemText>{t('Deactivate in team')}</ListItemText>
                </MenuItem>,
              );
            }

            if (canManageLeader) {
              items.push(
                <MenuItem
                  key="remove"
                  onClick={() => {
                    setConfirmRemoveId(team.id);
                    closeMenu();
                  }}
                  sx={{ color: 'error.main' }}
                >
                  <ListItemIcon sx={{ color: 'error.main' }}>
                    <Trash2 size={14} />
                  </ListItemIcon>
                  <ListItemText>{t('Remove from team')}</ListItemText>
                </MenuItem>,
              );
            }

            return items;
          })()}
      </Menu>

      <SlideDialog
        open={confirmRemoveId != null}
        onClose={() => setConfirmRemoveId(null)}
        title={t('Remove from team?')}
        maxWidth="xs"
        onSave={async () => {
          if (!confirmRemoveId) {
            return;
          }
          await removeTeamUser({
            variables: { team_id: confirmRemoveId, user_id: user.id },
          });
          setConfirmRemoveId(null);
        }}
        loading={removingLoading}
        saveColor="error"
        saveText={t('Remove')}
        saveIcon={<Trash2 />}
      >
        <Box sx={{ p: 2 }}>
          <Typography variant="body2">
            {t(
              'Are you sure you want to remove this user from "{{teamName}}"? They will lose access to team-related data. If this is their only team, they will also lose their agent status.',
              { teamName: teamForConfirm?.name ?? t('Unnamed team') },
            )}
          </Typography>
        </Box>
      </SlideDialog>

      <MutationErrorSnackbar
        error={apolloError}
        onClose={() => setApolloError(null)}
      />
    </ListingAccordion>
  );
};
